import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogHeader,
  DialogBody,
  DialogFooter,
  Typography,
  Alert,
  Spinner,
} from '@material-tailwind/react';
import { DocumentArrowDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

import SectionList from './SectionList';
import DynamicSectionsEditor from './DynamicSectionsEditor';
import PreviewPane from './PreviewPane';
import SubjectAircraftPanel from './SubjectAircraftPanel';
import AnalysisConfigPanel from './AnalysisConfigPanel';
import AISettingsPanel from './AISettingsPanel';
import ListingsSummaryPanel from './ListingsSummaryPanel';
import AICostsPanel from './AICostsPanel';
import {
  CoverSection,
  MarketTrendsSection,
  OnMarketCompsSection,
  RecentlySoldCompsSection,
  SummaryPricingSection,
} from './sections';
import {
  updateSection,
  toggleSection,
  resetSection,
  getPreview,
  getShareablePreview,
  finalizeDraft,
  // regenerateWithExclusions,
  refreshAnalysis,
  regenerateCommentary,
  updateHiddenCharts,
} from '../../services/reportApi';

// Convert sections object to array format for SectionList
function sectionsToArray(sectionsObj) {
  if (!sectionsObj) return [];
  return Object.entries(sectionsObj).map(([id, section]) => ({
    id,
    ...section,
  }));
}

function ReportEditor({ draft, onDraftChange, onFinalized }) {
  const [selectedSection, setSelectedSection] = useState('draftSettings'); // Default to draft settings
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [shareableHtml, setShareableHtml] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isShareableLoading, setIsShareableLoading] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  const [error, setError] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [shareableError, setShareableError] = useState(null);
  const [showFinalizeDialog, setShowFinalizeDialog] = useState(false);
  const [isApplyingExclusions] = useState(false);
  const [hasDraftChanges, setHasDraftChanges] = useState(false);
  const [hasGuidelinesChanges, setHasGuidelinesChanges] = useState(false);
  const [pendingEditedComps, setPendingEditedComps] = useState(undefined); // undefined = no changes, array = edited comps
  const [excludedListings, setExcludedListings] = useState([]); // full exclusion list, synced from draft on load

  // Track current values from panels (for intelligent refresh)
  const [currentSubjectAircraft, setCurrentSubjectAircraft] = useState(null);
  const [currentAnalysisConfig, setCurrentAnalysisConfig] = useState(null);
  const [hasAnalysisConfigChanges, setHasAnalysisConfigChanges] = useState(false);
  const [currentGuidelines, setCurrentGuidelines] = useState(null);
  const [pendingSalesCompExclusions, setPendingSalesCompExclusions] = useState(null); // null = no changes, array = pending exclusions

  const draftId = draft?.draftId;

  // Convert sections object to array
  const sectionsArray = useMemo(() => sectionsToArray(draft?.sections), [draft?.sections]);

  // Exclusion arrays from draft document (Firestore field names)
  const draftExcludedListingRegs = useMemo(
    () => draft?.excludedListings ?? [],
    [draft?.excludedListings]
  );
  const draftExcludedSalesCompRegs = useMemo(
    () => draft?.excludedSalesComps ?? [],
    [draft?.excludedSalesComps]
  );

  // Load print preview
  const loadPreview = useCallback(async () => {
    if (!draftId) return;
    try {
      setIsPreviewLoading(true);
      setPreviewError(null);
      const html = await getPreview(draftId);
      setPreviewHtml(html);
    } catch (err) {
      setPreviewError(err.message);
    } finally {
      setIsPreviewLoading(false);
    }
  }, [draftId]);

  // Load shareable/client preview
  const loadShareablePreview = useCallback(async () => {
    if (!draftId) return;
    try {
      setIsShareableLoading(true);
      setShareableError(null);
      const html = await getShareablePreview(draftId);
      setShareableHtml(html);
    } catch (err) {
      setShareableError(err.message);
    } finally {
      setIsShareableLoading(false);
    }
  }, [draftId]);

  // Load both previews on draft change
  useEffect(() => {
    if (draft) {
      loadPreview();
      loadShareablePreview();
    }
  }, [draft, loadPreview, loadShareablePreview]);

  // Handle section toggle
  const handleToggleSection = async (sectionId, included) => {
    try {
      await toggleSection(draftId, sectionId, included);
      // Update local draft state
      if (onDraftChange) {
        onDraftChange((prev) => ({
          ...prev,
          sections: {
            ...prev.sections,
            [sectionId]: {
              ...prev.sections[sectionId],
              included,
            },
          },
        }));
      }
      loadPreview();
      loadShareablePreview();
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle section content update
  const handleUpdateSection = async (sectionId, content) => {
    try {
      setIsSaving(true);
      const response = await updateSection(draftId, sectionId, content);
      // Update local draft state with response or sent content
      if (onDraftChange) {
        const updatedContent = response?.content || { ...draft?.sections?.[sectionId]?.content, ...content };
        onDraftChange((prev) => ({
          ...prev,
          sections: {
            ...prev.sections,
            [sectionId]: {
              ...prev.sections[sectionId],
              content: updatedContent,
            },
          },
        }));
      }
      // Await the preview refresh
      await Promise.all([loadPreview(), loadShareablePreview()]);
    } catch (err) {
      console.error('Update section error:', err);
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle section reset
  const handleResetSection = async (sectionId) => {
    try {
      setIsSaving(true);
      const resetData = await resetSection(draftId, sectionId);
      // Update local draft state with original content
      if (onDraftChange) {
        onDraftChange((prev) => ({
          ...prev,
          sections: {
            ...prev.sections,
            [sectionId]: {
              ...prev.sections[sectionId],
              content: resetData.content || prev.sections[sectionId].originalContent,
            },
          },
        }));
      }
      loadPreview();
      loadShareablePreview();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle finalization
  const handleFinalize = async () => {
    try {
      setIsFinalizing(true);
      const result = await finalizeDraft(draftId);
      setShowFinalizeDialog(false);
      if (onFinalized) {
        onFinalized(result);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsFinalizing(false);
    }
  };

  // Queue exclusions from iframe chart selections (applied on manual refresh)
  const queueExclusionsFromIframe = useCallback((newExcludedIds) => {
    if (!newExcludedIds || newExcludedIds.length === 0) return;

    setExcludedListings((prev) => {
      const combined = [...new Set([...prev, ...newExcludedIds])];
      return combined;
    });
  }, []);

  // Listen for excludePoints events from iframe - queue for manual refresh
  useEffect(() => {
    const handleExcludePointsEvent = (event) => {
      const excludedIds = event.detail?.excludedIds || [];
      if (excludedIds.length > 0) {
        queueExclusionsFromIframe(excludedIds);
      }
    };

    window.addEventListener('excludePoints', handleExcludePointsEvent);
    return () => {
      window.removeEventListener('excludePoints', handleExcludePointsEvent);
    };
  }, [queueExclusionsFromIframe]);

  // Mark a listing for restoration (will be applied on next refresh)
  const handleRestoreExclusion = useCallback((registrationToRestore) => {
    const regUpper = registrationToRestore?.toUpperCase();
    const listing = (draft?.listingsSummary?.excludedListings || [])
      .find(l => (l.reg || l.registration)?.toUpperCase() === regUpper);

    // Only allow reverting user-excluded listings (not system exclusions like missing_price)
    if (listing && listing.reason !== 'user_excluded') return;

    setExcludedListings((prev) => prev.filter(r => r.toUpperCase() !== regUpper));
  }, [draft?.listingsSummary?.excludedListings]);

  // Mark all user-excluded listings for restoration (will be applied on next refresh)
  const handleRevertAllExclusions = useCallback(() => {
    const userExcludedRegs = (draft?.listingsSummary?.excludedListings || [])
      .filter(listing => listing.reason === 'user_excluded' && (listing.price_usd || listing.price))
      .map(listing => (listing.reg || '').toUpperCase());

    setExcludedListings((prev) => prev.filter(r => !userExcludedRegs.includes(r.toUpperCase())));
  }, [draft?.listingsSummary?.excludedListings]);

  // Toggle chart visibility (immediately persisted, no refresh needed for data)
  const handleToggleChart = useCallback(async (chartId) => {
    if (!draftId) return;

    // Use functional update to get latest state
    const currentHidden = draft?.hiddenCharts || [];
    const isCurrentlyHidden = currentHidden.includes(chartId);
    const newHidden = isCurrentlyHidden
      ? currentHidden.filter(id => id !== chartId)  // Show
      : [...currentHidden, chartId];                 // Hide

    try {
      const response = await updateHiddenCharts(draftId, newHidden);

      // Update local state with API response
      if (onDraftChange && response.hiddenCharts !== undefined) {
        onDraftChange((prev) => ({
          ...prev,
          hiddenCharts: response.hiddenCharts,
        }));
      }

      // Refresh preview to show updated charts
      await Promise.all([loadPreview(), loadShareablePreview()]);
    } catch (err) {
      console.error('Failed to update hidden charts:', err);
      setError(err.message);
    }
  }, [draftId, draft?.hiddenCharts, onDraftChange, loadPreview, loadShareablePreview]);

  // Handle sales comp exclusion toggle (local state only, applied on refresh)
  const handleToggleSalesCompExclusion = useCallback((reg) => {
    if (!reg) return;

    const regUpper = reg.toUpperCase();
    // Use pending state if exists, otherwise start from draft state
    const currentExcluded = pendingSalesCompExclusions ?? draftExcludedSalesCompRegs ?? [];
    const newExcluded = currentExcluded.includes(regUpper)
      ? currentExcluded.filter(r => r !== regUpper)  // Re-include
      : [...currentExcluded, regUpper];              // Exclude

    setPendingSalesCompExclusions(newExcluded);
  }, [pendingSalesCompExclusions, draftExcludedSalesCompRegs]);

  // Handle on-market comp exclusion toggle (local state only, applied on refresh)
  const handleToggleListingExclusion = useCallback((reg) => {
    if (!reg) return;
    const regUpper = reg.toUpperCase();

    setExcludedListings((prev) =>
      prev.includes(regUpper)
        ? prev.filter(r => r !== regUpper)
        : [...prev, regUpper]
    );
  }, []);

  // Handle on-market comps change (local state only, submitted on refresh)
  const handleCompsChange = useCallback((editedComps) => {
    setPendingEditedComps(editedComps);
  }, []);

  // Reset to AI-selected comparables (reads original content, clears exclusions)
  const handleResetCompsToAI = useCallback(() => {
    const originalComps = draft?.sections?.onMarketComps?.originalContent?.comparables
      || draft?.sections?.onMarketComps?.originalContent?.listings
      || [];
    setPendingEditedComps(originalComps);
    setExcludedListings([]);
  }, [draft?.sections?.onMarketComps?.originalContent]);

  // Reset pending comps when draft changes (e.g., after refresh)
  useEffect(() => {
    setPendingEditedComps(undefined);
  }, [draft?.draftId, draft?.sections?.onMarketComps?.content]);

  // Sync local exclusion list from draft (on load and after refresh)
  useEffect(() => {
    setExcludedListings(draftExcludedListingRegs);
  }, [draftExcludedListingRegs]);

  // Reset pending sales comp exclusions when draft changes
  useEffect(() => {
    setPendingSalesCompExclusions(null);
  }, [draft?.draftId, draftExcludedSalesCompRegs]);

  // Reset draft changes flag when draft is refreshed
  useEffect(() => {
    setHasDraftChanges(false);
  }, [draft?.draftId, draft?.subjectAircraft]);

  // Reset guidelines changes flag when draft guidelines are updated
  useEffect(() => {
    setHasGuidelinesChanges(false);
  }, [draft?.draftId, draft?.customGuidelines]);

  // Check if there are pending comp changes (compare registrations, not just presence)
  const hasCompChanges = useMemo(() => {
    if (pendingEditedComps === undefined) return false;
    const draftComps = draft?.sections?.onMarketComps?.content?.comparables
      || draft?.sections?.onMarketComps?.content?.listings
      || [];
    if (pendingEditedComps.length !== draftComps.length) return true;
    const pendingRegs = pendingEditedComps.map(c => (c.reg || c.registration || '').toUpperCase()).sort();
    const draftRegs = draftComps.map(c => (c.reg || c.registration || '').toUpperCase()).sort();
    return JSON.stringify(pendingRegs) !== JSON.stringify(draftRegs);
  }, [pendingEditedComps, draft?.sections?.onMarketComps?.content]);

  // Check if there are pending exclusion changes
  const hasExclusionChanges = JSON.stringify(excludedListings.map(r => r.toUpperCase()).sort()) !== JSON.stringify(draftExcludedListingRegs.map(r => r.toUpperCase()).sort());

  // Check if there are pending sales comp exclusion changes
  const hasSalesCompExclusionChanges = pendingSalesCompExclusions !== null;

  // Compute effective on-market comps content (with pending changes applied)
  const effectiveOnMarketCompsContent = useMemo(() => {
    const currentContent = draft?.sections?.onMarketComps?.content;
    if (!currentContent) return null;
    if (pendingEditedComps === undefined) return currentContent;
    return {
      ...currentContent,
      comparables: pendingEditedComps,
    };
  }, [draft?.sections?.onMarketComps?.content, pendingEditedComps]);

  const effectiveExcludedListings = excludedListings;

  // Determine what type of changes exist
  const hasDataChanges = hasDraftChanges || hasAnalysisConfigChanges || hasCompChanges || hasExclusionChanges || hasSalesCompExclusionChanges;
  const hasAnyChanges = hasDataChanges || hasGuidelinesChanges;

  // Single intelligent refresh handler
  const handleRefreshClick = useCallback(async () => {
    if (!draftId || !hasAnyChanges) return;

    try {
      setIsRefreshing(true);
      setError(null);

      if (hasDataChanges) {
        // Data changed (with or without guidelines) - full refresh
        const params = {
          subjectRegistration: currentSubjectAircraft?.registration,
          subjectManufacturer: currentSubjectAircraft?.manufacturer,
          subjectModel: currentSubjectAircraft?.model,
          subjectYear: currentSubjectAircraft?.year,
          subjectHours: currentSubjectAircraft?.hours,
          yearRange: currentAnalysisConfig?.yearRange ?? draft?.analysisConfig?.yearRange,
          subjectAircraftDetails: currentSubjectAircraft?.additionalDetails,
          excludedListings,
          periodDays: currentAnalysisConfig?.periodDays ?? draft?.analysisConfig?.periodDays,
        };

        // Include guidelines if they've changed
        if (hasGuidelinesChanges && currentGuidelines) {
          params.customGuidelines = currentGuidelines;
        }

        // Include edited comps if any
        if (pendingEditedComps !== undefined) {
          params.editedComps = pendingEditedComps;
        }

        // Include sales comp exclusions if changed
        if (pendingSalesCompExclusions !== null) {
          params.excludedSalesComps = pendingSalesCompExclusions;
        }

        const response = await refreshAnalysis(draftId, params);

        if (response?.draft && onDraftChange) {
          onDraftChange(response.draft);
        }

        // Clear pending state after successful refresh
        // (excludedListings will sync from draft via useEffect)
        setPendingEditedComps(undefined);
        setPendingSalesCompExclusions(null);
      } else if (hasGuidelinesChanges && currentGuidelines) {
        // Only guidelines changed - regenerate commentary only
        const response = await regenerateCommentary(draftId, currentGuidelines);

        if (response.success && onDraftChange) {
          onDraftChange((prev) => {
            const updated = {
              ...prev,
              customGuidelines: response.guidelinesUsed,
              aiPrompt: response.promptUsed,
            };
            // Apply commentary updates (includes summaryPricing with estimatedValue, etc.)
            if (response.commentary) {
              updated.sections = { ...prev.sections };
              Object.entries(response.commentary).forEach(([sectionId, content]) => {
                if (updated.sections[sectionId]) {
                  updated.sections[sectionId] = {
                    ...updated.sections[sectionId],
                    content,
                  };
                }
              });
            }
            return updated;
          });
        }
      }

      // Refresh previews after any change
      await Promise.all([loadPreview(), loadShareablePreview()]);
    } catch (err) {
      console.error('Refresh failed:', err);
      setError(err.message);
    } finally {
      setIsRefreshing(false);
    }
  }, [draftId, hasAnyChanges, hasDataChanges, hasGuidelinesChanges, currentSubjectAircraft, currentAnalysisConfig, currentGuidelines, pendingEditedComps, excludedListings, pendingSalesCompExclusions, draft?.analysisConfig, onDraftChange, loadPreview, loadShareablePreview]);

  // Get current section data
  const currentSection = draft?.sections?.[selectedSection];

  // Render editor based on section type
  const renderEditor = () => {
    if (!selectedSection || !currentSection) {
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <Typography>Select an editable section to edit</Typography>
        </div>
      );
    }

    // Cover page editor
    if (selectedSection === 'cover') {
      return (
        <CoverSection
          content={currentSection.content}
          onSave={(content) => handleUpdateSection('cover', content)}
          onReset={() => handleResetSection('cover')}
          isSaving={isSaving}
        />
      );
    }

    // Always show section viewers for data-driven sections
    if (selectedSection === 'marketTrends') {
      return (
        <MarketTrendsSection
          content={currentSection.content}
          listingsSummary={draft?.listingsSummary}
          hiddenCharts={draft?.hiddenCharts || []}
          onToggleChart={handleToggleChart}
          onTrendPointClick={(chartType) => {
            // TODO: Open source listings modal
          }}
          onRestoreExclusion={handleRestoreExclusion}
          isApplyingExclusions={isApplyingExclusions}
          onSave={(content) => handleUpdateSection('marketTrends', content)}
          onReset={() => handleResetSection('marketTrends')}
          isSaving={isSaving}
        />
      );
    }
    if (selectedSection === 'onMarketComps') {
      return (
        <OnMarketCompsSection
          content={effectiveOnMarketCompsContent}
          originalContent={currentSection.originalContent || currentSection.content}
          onCompsChange={handleCompsChange}
          onResetToAI={handleResetCompsToAI}
          excludedListings={effectiveExcludedListings}
          onToggleExclusion={handleToggleListingExclusion}
          isUpdating={false}
          hasLocalChanges={hasCompChanges || hasExclusionChanges}
        />
      );
    }
    if (selectedSection === 'recentlySoldComps') {
      return (
        <RecentlySoldCompsSection
          content={currentSection.content}
          allSales={draft?.rawData?.allSales}
          excludedSalesComps={pendingSalesCompExclusions ?? draftExcludedSalesCompRegs ?? []}
          onToggleExclusion={handleToggleSalesCompExclusion}
        />
      );
    }

    if (!currentSection.editable) {
      // Default message for other non-editable sections
      return (
        <div className="flex items-center justify-center h-full text-gray-500">
          <Typography>This section is not editable (data-driven)</Typography>
        </div>
      );
    }

    if (selectedSection === 'about') {
      return (
        <DynamicSectionsEditor
          title="About Lone Mountain"
          sections={currentSection.content?.sections || []}
          specialFields={{
            subtitle: currentSection.content?.subtitle || '',
          }}
          specialFieldsConfig={[
            { name: 'subtitle', label: 'Subtitle', type: 'input' },
          ]}
          onSave={(content) => handleUpdateSection(selectedSection, content)}
          onReset={() => handleResetSection(selectedSection)}
          isSaving={isSaving}
        />
      );
    }

    if (selectedSection === 'summaryPricing') {
      return (
        <SummaryPricingSection
          content={currentSection.content}
          onSave={(content) => handleUpdateSection('summaryPricing', content)}
          onReset={() => handleResetSection('summaryPricing')}
          isSaving={isSaving}
        />
      );
    }

    if (selectedSection === 'strategy') {
      return (
        <DynamicSectionsEditor
          title="Brokerage Services"
          sections={currentSection.content?.sections || []}
          specialFields={{
            brokerageFee: currentSection.content?.brokerageFee || '',
          }}
          specialFieldsConfig={[
            { name: 'brokerageFee', label: 'Brokerage Fee', type: 'input' },
          ]}
          onSave={(content) => handleUpdateSection(selectedSection, content)}
          onReset={() => handleResetSection(selectedSection)}
          isSaving={isSaving}
        />
      );
    }

    return (
      <div className="flex items-center justify-center h-full text-gray-500">
        <Typography>Editor not available for this section type</Typography>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      {error && (
        <Alert color="red" className="mb-4" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-6">
          <div>
            <Typography variant="h5" color="blue-gray">
              Report Editor
            </Typography>
            <Typography variant="small" color="gray">
              {draft?.customerName} - {draft?.aircraftModel}
            </Typography>
          </div>
          {(draft?.sections?.summaryPricing?.content?.estimatedValue || draft?.sections?.summaryPricing?.content?.estimated_value) && (
            <div className="px-4 py-2 bg-blue-50 rounded-lg border border-blue-200">
              <Typography variant="small" color="blue" className="text-xs font-medium">
                Estimated Value
              </Typography>
              <Typography variant="h6" color="blue">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
                  draft?.sections?.summaryPricing?.content?.estimatedValue || draft?.sections?.summaryPricing?.content?.estimated_value
                )}
              </Typography>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={hasAnyChanges ? 'filled' : 'outlined'}
            color={hasAnyChanges ? 'blue' : 'gray'}
            className="flex items-center gap-2"
            onClick={handleRefreshClick}
            disabled={!hasAnyChanges || isRefreshing}
          >
            {isRefreshing ? (
              <>
                <Spinner className="h-4 w-4" />
                Refreshing...
              </>
            ) : hasAnyChanges ? (
              <>
                <ArrowPathIcon className="h-5 w-5" />
                {hasDataChanges ? 'Refresh Analysis' : 'Update Commentary'}
              </>
            ) : (
              <>
                <ArrowPathIcon className="h-5 w-5" />
                No Changes
              </>
            )}
          </Button>
          <Button
            className="flex items-center gap-2"
            onClick={() => setShowFinalizeDialog(true)}
          >
            <DocumentArrowDownIcon className="h-5 w-5" />
            Finalize Report
          </Button>
        </div>
      </div>

      {/* Main content area */}
      <div className="flex-1 grid grid-cols-12 gap-4 min-h-0">
        {/* Section list - left sidebar (narrow) */}
        <div className="col-span-2 overflow-auto">
          <SectionList
            sections={sectionsArray}
            selectedSection={selectedSection}
            onSelectSection={setSelectedSection}
            onToggleSection={handleToggleSection}
            hasDraftChanges={hasDraftChanges || hasAnalysisConfigChanges}
            hasCompChanges={hasCompChanges}
            hasGuidelinesChanges={hasGuidelinesChanges}
            hasExclusionChanges={hasExclusionChanges}
            pendingExclusionsCount={Math.abs(excludedListings.length - draftExcludedListingRegs.length)}
            hasSalesCompExclusionChanges={hasSalesCompExclusionChanges}
            isLocked={isRefreshing}
            periodDays={draft?.analysisConfig?.periodDays}
          />
        </div>

        {/* Editor - center (narrow) */}
        <div className="col-span-4 overflow-auto relative">
          {/* Loading overlay when refreshing */}
          {isRefreshing && (
            <div className="absolute inset-0 bg-white/70 z-10 flex items-center justify-center">
              <div className="flex flex-col items-center gap-2">
                <Spinner className="h-8 w-8" />
                <Typography variant="small" color="blue-gray">
                  Refreshing analysis...
                </Typography>
              </div>
            </div>
          )}
          {/* Keep SubjectAircraftPanel and AnalysisConfigPanel mounted to preserve form state */}
          <div className={selectedSection === 'draftSettings' ? '' : 'hidden'}>
            <SubjectAircraftPanel
              draft={draft}
              onValuesChange={setCurrentSubjectAircraft}
              onDataChanged={setHasDraftChanges}
            />
            <AnalysisConfigPanel
              draft={draft}
              onValuesChange={setCurrentAnalysisConfig}
              onDataChanged={setHasAnalysisConfigChanges}
            />
          </div>
          {/* Keep AISettingsPanel mounted to preserve form state */}
          <div className={selectedSection === 'aiSettings' ? 'h-full' : 'hidden'}>
            <AISettingsPanel
              draft={draft}
              onGuidelinesChange={setCurrentGuidelines}
              onHasChanges={setHasGuidelinesChanges}
              onError={setError}
            />
          </div>
          {selectedSection === 'aiCosts' && <AICostsPanel draft={draft} />}
          {selectedSection === 'listings' && (
            <ListingsSummaryPanel
              listingsSummary={draft?.listingsSummary}
              periodDays={draft?.analysisConfig?.periodDays}
              onRevertExclusion={handleRestoreExclusion}
              onRevertAllExclusions={handleRevertAllExclusions}
              effectiveExcludedListings={excludedListings}
              draftExcludedListingRegs={draftExcludedListingRegs}
              hasExclusionChanges={hasExclusionChanges}
            />
          )}
          {selectedSection !== 'draftSettings' && selectedSection !== 'aiSettings' && selectedSection !== 'aiCosts' && selectedSection !== 'listings' && renderEditor()}
        </div>

        {/* Preview - right (expanded) */}
        <div className="col-span-6 overflow-hidden">
          <PreviewPane
            htmlContent={previewHtml}
            shareableContent={shareableHtml}
            isLoading={isPreviewLoading}
            isLoadingShareable={isShareableLoading}
            isApplyingExclusions={isApplyingExclusions}
            error={previewError}
            errorShareable={shareableError}
            onRefresh={loadPreview}
            onRefreshShareable={loadShareablePreview}
            selectedSection={selectedSection}
          />
        </div>
      </div>

      {/* Finalize confirmation dialog */}
      <Dialog open={showFinalizeDialog} handler={setShowFinalizeDialog}>
        <DialogHeader>Finalize Report</DialogHeader>
        <DialogBody>
          <Typography>
            Are you sure you want to finalize this report? This will generate the
            final PDF and the report will no longer be editable.
          </Typography>
        </DialogBody>
        <DialogFooter className="gap-2">
          <Button
            variant="outlined"
            onClick={() => setShowFinalizeDialog(false)}
            disabled={isFinalizing}
          >
            Cancel
          </Button>
          <Button onClick={handleFinalize} loading={isFinalizing}>
            Finalize
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  );
}

export default ReportEditor;
